import { CheckIcon } from '@heroicons/react/24/outline';

import { Button } from '@oysterjs/ui/button';
import { DialogActions, DialogBody, DialogDescription, DialogTitle } from '@oysterjs/ui/dialog';
import { SubscriptionPlan } from '~/api/graphql';
import { UsageBasedFeaturesNote } from './usage-based-features-note';

/** Static Basic + Pro feature matrix for renewal migration (no pricing or seat controls). */
const RENEWAL_PLAN_COMPARISON = [
  {
    id: SubscriptionPlan.Basic,
    name: 'Cara Basic',
    description: 'The essentials to provide your best work for clients.',
    features: {
      Copilot: [
        'All built-in presets included',
        '3 custom presets included',
        '1 document style included',
        'Standard processing priority'
      ],
      'Voice AI': [
        '1 voice agent included',
        'Additional voice agents for $100/month',
        'Up to 500 minutes/month per agent',
        'Basic self-serve customization',
        'Static call notification routing',
        'Standard call priority'
      ],
      'Email AI': ['Up to 250 emails/month'],
      Knowledge: ['Up to 500 pages'],
      'Security and Privacy': ['SOC2 Compliance', 'No cross-account data access for training'],
      Support: [
        'Under 3 business days response time',
        'Email and Slack support',
        'Online documentation'
      ]
    }
  },
  {
    id: SubscriptionPlan.Pro,
    name: 'Cara Pro',
    description: 'A plan that scales with your rapidly growing business.',
    featured: true,
    features: {
      Copilot: [
        'All built-in presets included',
        'Unlimited custom presets included',
        'Unlimited document styles included',
        'Higher processing priority'
      ],
      'Voice AI': [
        '1 voice agent included',
        'Additional voice agents for $100/month',
        'Up to 1,000 minutes/month per agent',
        'Advanced self-serve customization',
        'Dynamic call notification routing',
        'Reference customer info during the call',
        'Higher call priority'
      ],
      'Email AI': ['Up to 2,500 emails/month'],
      Knowledge: ['Up to 5,000 pages', 'Unlimited AMS & CRM integrations', 'Client portal app'],
      'Security and Privacy': [
        'SOC2 Compliance',
        'No cross-account data access for training',
        'Access to compliance and DPA docs'
      ],
      Support: [
        'Next business day response time',
        'Email support',
        'Online documentation',
        'Implementation consultation from the AI engineering team'
      ]
    }
  }
] as const;

export const RenewalPlanDetailsBody = () => (
  <>
    <DialogTitle>Plan details</DialogTitle>
    <DialogDescription>
      Compare Cara Basic and Cara Pro. Pricing and seat selection are configured in the renewal
      wizard.
    </DialogDescription>
    <DialogBody>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {RENEWAL_PLAN_COMPARISON.map((tier) => (
          <div
            key={tier.id}
            className="rounded-2xl p-6 ring-1 ring-neutral-200 dark:bg-neutral-800/50 dark:ring-white/15"
          >
            <div className="flex items-center justify-between gap-x-3">
              <h3 className="m-0 text-lg font-semibold text-neutral-900 dark:text-white">
                {tier.name}
              </h3>
              {'featured' in tier && tier.featured && (
                <p className="m-0 rounded-full bg-primary-600/10 px-2.5 py-1 text-xs font-medium text-primary-600 dark:bg-primary-500 dark:text-white">
                  Recommended
                </p>
              )}
            </div>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
              {tier.description}
            </p>
            <div className="mt-6 space-y-6">
              {Object.entries(tier.features).map(([category, items]) => (
                <div key={category}>
                  <h4 className="mb-3 text-sm font-medium text-neutral-900 dark:text-white">
                    {category}
                  </h4>
                  <ul className="space-y-2">
                    {items.map((feature) => (
                      <li key={feature} className="flex items-start gap-x-2">
                        <CheckIcon
                          aria-hidden="true"
                          className="mt-0.5 h-4 w-4 flex-none text-primary-600 dark:text-primary-400"
                        />
                        <span className="text-sm text-neutral-600 dark:text-neutral-300">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <UsageBasedFeaturesNote className="mt-6" />
    </DialogBody>
  </>
);

export const RenewalPlanDetailsActions = ({ onBack }: { onBack: () => void }) => (
  <DialogActions>
    <Button color="white" type="button" onClick={onBack}>
      Back to renewal options
    </Button>
  </DialogActions>
);
